import { Card } from '@mui/material';
import { memo } from 'react';
import { useDispatch } from 'react-redux';
import { docketActions, selectTabPreferences } from 'redux/slices/docketSlice';
import { todosAppSelectors } from 'redux/slices/todoSlice';
import { useAppSelector } from 'redux/store-hooks';
import { useDocketSelectors } from 'redux/utils/selectors/docket-selectors';
import TabbedPanel, { TabDefinition } from 'toolkit/tabs/tabbedPanel';
import { useDocketDateRange } from '../docket-day-hook';
import DocketContactSoon from './DocketContactSoon';
import DocketFollowUpEvents from './DocketFollowUpEvents';
import DocketLateEvents from './DocketLateEvents';
import DocketPendingEvents from './DocketPendingEvents';
import { Badge } from '@act/storybook';
import DocketNeeds from './DocketNeeds';
import { needsSelectors } from 'redux/slices/needs.slice';

interface DocketEventProps {
  guideId: string;
}

const DocketEvents = memo(({ guideId }: DocketEventProps) => {
  const { isFuture } = useDocketDateRange();

  const tabPreferences = useAppSelector(selectTabPreferences);
  const dispatch = useDispatch();
  const followUpCount =
    todosAppSelectors.useSelectIncompleteTodosForFollowUp(guideId)?.length;
  const lateCount =
    useDocketSelectors.latePendingItemsForGuide(guideId)?.length;
  const needsCount = Object.keys(
    needsSelectors.useSelectExpiringAndNeedsActionUserNeedsForGuide() ?? {}
  ).length;

  const tabDefinitions: TabDefinition[] = [
    {
      key: 'follow-up-events',
      label: 'Follow up',
      badge: followUpCount > 0 && !isFuture && (
        <Badge badgeContent={followUpCount} color="neutral" />
      ),
      component: <DocketFollowUpEvents guideId={guideId} />,
      disabled: isFuture,
    },
    {
      key: 'pending-events',
      label: 'Pending events',
      component: <DocketPendingEvents guideId={guideId} />,
    },
    {
      key: 'late-events',
      label: 'Late events',
      badge: lateCount > 0 && !isFuture && (
        <Badge badgeContent={lateCount} color="neutral" />
      ),
      component: <DocketLateEvents guideId={guideId} />,
      disabled: isFuture,
    },
    {
      key: 'needs',
      label: 'Needs',
      badge: needsCount > 0 && (
        <Badge badgeContent={needsCount} color="warning" />
      ),
      component: <DocketNeeds />,
    },
    {
      key: 'outreach',
      label: 'Outreach',
      component: <DocketContactSoon guideId={guideId} />,
      disabled: isFuture,
      ddTrackingName: 'Outreach-docketTab',
    },
  ];

  return (
    <Card
      variant="outlined"
      sx={{
        flexShrink: 0,
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'visible',
        '& .MuiCardContent-root': {
          padding: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        },
        // Fix the Box wrapping Tabs - remove padding
        '& > div > div:first-of-type': {
          paddingRight: '0 !important',
          maxWidth: '100%',
          overflow: 'hidden',
        },
        // Ensure tabs root takes full width
        '& .MuiTabs-root': {
          width: '100%',
          minWidth: 0,
        },
        // Allow the scroller to scroll and show scrollbar
        '& .MuiTabs-scroller': {
          overflow: 'auto !important',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '3px',
          },
        },
        // Prevent tabs from wrapping or shrinking
        '& .MuiTabs-flexContainer': {
          flexWrap: 'nowrap',
          gap: 0,
        },
        '& .MuiTab-root': {
          flexShrink: 0,
          flexGrow: 0,
          minWidth: '120px', // Force minimum width to ensure overflow
          padding: '12px 20px',
          whiteSpace: 'nowrap',
          // Remove the problematic negative margin
          '&:last-child': {
            marginRight: '0 !important',
          },
        },
      }}
    >
      <TabbedPanel
        tabDefinitions={tabDefinitions}
        currentTabKey={tabPreferences.tab}
        setCurrentTabKey={newVal =>
          void dispatch(docketActions.docketTabSelected({ tab: newVal }))
        }
      />
    </Card>
  );
});

DocketEvents.displayName = 'DocketEvents';

export default DocketEvents;
